import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import { List, ListItem, ListItemText, ListItemIcon, Box } from '@material-ui/core';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import InputIcon from '@material-ui/icons/InputOutlined';
import ImageIcon from '@material-ui/icons/Image';
import ImageOutlinedIcon from '@material-ui/icons/ImageOutlined';
import TextFieldIcon from '@material-ui/icons/TextFields';
import WrapTextIcon from '@material-ui/icons/WrapText';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import GridOnIcon from '@material-ui/icons/GridOn';
import DropDownIcon from '@material-ui/icons/ArrowDropDownCircle';
import ButtonIcon from '@material-ui/icons/Crop169Rounded';

import { UiCardItemType } from '../redux/uiCardItemDbSlice';
import { deepCopy } from '../general';
import { ListItemType, ListHash } from '../card_components/DesignUiCard';

type ItemWrapperProps = {
  lineLength: number;
  dragging: boolean;
  overlapped: boolean;
  translateValue: { x: number; y: number; };
}
const ItemWrapper = styled.div<ItemWrapperProps>`
  position: absolute;
  width: 100%;
  height: ${props => props.lineLength * 48 + 'px'};
  background-color: ${props => props.overlapped ? '#eeeeee' : undefined};
  transform: ${props => 'translate(' + props.translateValue.x + 'px,' + props.translateValue.y + 'px) !important'};
  transition: ${props => props.dragging ? undefined : '300ms'};
  pointer-events: none;
`
type ItemListItemProps = {
  dragging?: boolean;
}
const ItemListItem = styled(ListItem)<ItemListItemProps>`
  position: absolute;
  white-space: nowrap;
  overflow: hidden;
  transition: ${props => props.dragging ? undefined : '300ms'};
  pointer-events: auto;
`
const ShortListItemIcon = styled(ListItemIcon)`
  margin-right: -16px;
`

//デザインタブ-アイテムタブ
type SelectUiItemNameProps = {
  cardId: string;
  ids: string[];
  originList: ListHash;
  list: ListHash;
  setList: (list: ListHash) => void;
  selectingId: string | undefined;
  setSelectingId: (id: string | undefined) => void;
  changeItemZ: (data: DraggableData) => void;
}
const SelectUiItemName: React.FC<SelectUiItemNameProps> = ({cardId, ids, originList, list, setList, selectingId, setSelectingId, changeItemZ}) => {
  const [draggingId, setDraggingId] = useState('');
  const [overlappedId, setOverlappedId] = useState('');
  const [startData, setStartData] = useState<DraggableData | undefined>(undefined);
  const [draggingData, setDraggingData] = useState<DraggableData | undefined>(undefined);

  useEffect(() => setList(originList), [draggingId]);

  const handleDragStart = (id: string, data: DraggableData) => {
    setSelectingId(id);
    setDraggingId(id);
    setStartData(data);
    setDraggingData(data);
  }
  const changeZExe = (data: DraggableData) => {
    setDraggingId('');
    setOverlappedId('');
    if (!startData) return;
    changeItemZ({ ...data, deltaX: data.x - startData.x, deltaY: data.y - startData.y, lastX: startData.x, lastY: startData.y });
    setStartData(undefined);
  }
  const changeList = (item: ListItemType, id: string, data: DraggableData) => {
    setDraggingData(data);
    const dragY = Math.round(data.lastY / 48);
    const dropY = Math.round(data.y / 48);
    const detailedDropY = data.y / 48;
    const newList = deepCopy(list);
    if (item.type !== 'group') {
      let overlapFlag = false;
      const overlapCondition = detailedDropY > item.order ? -0.5 : 0;
      ids.forEach(targetId => {
        const target = deepCopy(originList[targetId]);
        if (targetId === selectingId || !target) return;
        // update overlappedId
        const parentRangeTop = target.order + overlapCondition;
        const parentRangeBottom = parentRangeTop + 0.5 + target.childrenIds.length;
        if (target.type === 'group' && parentRangeTop <= detailedDropY && detailedDropY < parentRangeBottom) {
          setOverlappedId(targetId);
          overlapFlag = true;
          if (targetId !== item.parentId) {
            target.childrenIds.push(id);
            newList[targetId] = { ...newList[targetId], childrenIds: target.childrenIds };
            setList(newList);
          }
        }
      })
      if (!overlapFlag) setOverlappedId('');
    }

    // update selecting item's order
    newList[id].order = dropY;

    // update other items' order
    const lineLength = 1 + item.childrenIds.length;
    ids.forEach(targetId => {
      const target = list[targetId];
      if (targetId === selectingId || !target) return;
      const parentBottom = list[target.parentId] ? list[target.parentId].childrenIds.length - target.z - 1 : 0;
      const targetOrderBottom = target.order + target.childrenIds.length + parentBottom;
      const targetOrderTop = list[target.parentId] ? list[target.parentId].order : target.order;
      // update order
      if (dragY < dropY) {
        if (item.type !== 'group' && dragY < target.order && target.order <= dropY + item.childrenIds.length) {
          newList[targetId] = { ...target, order: target.order - lineLength };
        } else if (item.type === 'group' && dragY < targetOrderBottom && targetOrderBottom <= dropY + item.childrenIds.length) {
          newList[targetId] = { ...target, order: target.order - lineLength };
        }
      } else if (dragY > detailedDropY) {
        if (item.type !== 'group' && target.type === 'group' && detailedDropY < item.order) {
          if (dropY <= target.order && target.order <= dragY && detailedDropY < dropY) {
            newList[targetId] = { ...target, order: target.order + lineLength };
          }
        } else if (item.type === 'group' && dropY <= targetOrderTop && targetOrderTop < dragY) {
          newList[targetId] = { ...target, order: target.order + lineLength };
        } else if (item.type !== 'group' && dropY <= target.order && target.order < dragY) {
          newList[targetId] = { ...target, order: target.order + lineLength };
        }
      }
    })
    setList(newList);
  }
  const getTranslateValue = (id: string, item: ListItemType): { x: number, y: number } => {
    const x = item.parentId === cardId ? 0 : 24;
    if (id === draggingId && draggingData) return { x: x, y: draggingData.y };
    else if (item.parentId === draggingId && draggingData) return { x: x, y: draggingData.y + (item.z + 1) * 48 };
    else return { x: x, y: item.order * 48 };
  }
  const getIcon = (type: UiCardItemType) => {
    if (type === 'input') return <InputIcon style={{marginRight: 16}} />;
    if (type === 'multi') return <WrapTextIcon style={{marginRight: 16}} />;
    if (type === 'text') return <TextFieldIcon style={{marginRight: 16}} />;
    if (type === 'button') return <ButtonIcon style={{marginRight: 16}} />;
    if (type === 'checkbox') return <CheckBoxIcon style={{marginRight: 16}} />;
    if (type === 'dropdown') return <DropDownIcon style={{marginRight: 16}} />;
    if (type === 'variable') return <ImageIcon style={{marginRight: 16}} />;
    if (type === 'static') return <ImageOutlinedIcon style={{marginRight: 16}} />;
    if (type === 'group') return <GridOnIcon style={{marginRight: 16}} />;
  }

  return (
    <Box width="100%">
      <List component="nav">
        {/* リアルタイムにカードプレビューの描画を書き換えるならこっち */}
        {/* { ids.map(id => {
          const item = items[id];
          if (!item) return;
          return (
            <Draggable position={{x: item.parentId === cardId ? 0 : 24, y: item.z * 48}} handle=".handle"
              onStart={() => handleDragStart(id)}
              onDrag={(ev: DraggableEvent, data: DraggableData) => changeZExe(data)}
              onStop={() => setDraggingId('')}
              bounds={{top: 0, bottom: (ids.length - 1) * 48, left: 0, right: 24}}>
              <ItemListItem button key={id} selected={id === selectingId}
                onClick={() => setSelectingId(id)} dragging={id === draggingId}>
                <ListItemIcon className="handle">
                  <DragIndicatorIcon />
                </ListItemIcon>
                <ListItemText primary={item.name} key={item.name} />
              </ItemListItem>
            </Draggable>
          );
        }) } */}
        { ids.map(id => {
          const originListItem = originList[id];
          const item = list[id];
          if (!originListItem || !item) return undefined;
          return (
            <Draggable position={{x: item.parentId === cardId ? 0 : 24, y: item.order * 48}} handle=".handle"
              onStart={(ev: DraggableEvent, data: DraggableData) => handleDragStart(id, data)}
              onDrag={(ev: DraggableEvent, data: DraggableData) => changeList(originListItem, id, data)}
              onStop={(ev: DraggableEvent, data: DraggableData) => changeZExe(data)}
              axis="y" bounds={{top: -12, bottom: (ids.length - 1) * 48 + 12}}
            >
              <ItemWrapper lineLength={item.childrenIds.length + 1}
                dragging={id === draggingId || item.parentId === draggingId} overlapped={id === overlappedId}
                translateValue={getTranslateValue(id, item)}
                style={{zIndex: item.type === 'group' ? 0 : 1}}
              >
                <ItemListItem button key={id} selected={id === selectingId}
                  onClick={() => setSelectingId(id)}>
                  <ShortListItemIcon className="handle">
                    <DragIndicatorIcon />
                  </ShortListItemIcon>
                  { getIcon(item.type) }
                  <ListItemText primary={item.name} key={item.name} />
                </ItemListItem>
              </ItemWrapper>
            </Draggable>
          );
        }) }
      </List>
    </Box>
  );
}

export default SelectUiItemName;
